import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import LoadFailed from '../../teacher_payments/shared/load_failed';
import ReviewPill from '../../teacher_payments/penalties/review_pill';
import { fetch_penalties_session, post_issue_action } from '../../teacher_payments/api';
import { session_params } from '../../teacher_payments/penalties/requests';
import ChangePenaltyModal from './change_penalty_modal';
import TeacherPaymentsLink from './teacher_payments_link_view';
import {
  POPUP_EMPTY_TEXT,
  POPUP_TITLE,
  hides_strip,
  popup_summary_text,
  session_view,
  shows_view_here
} from './adapters';
import '../../teacher_payments/teacher_payments.css';
import '../../teacher_payments/penalties/penalties.css';
import './payment_penalties.css';

/*
 * the teacher-payment penalties strip on a session page: the review pill and the Change penalty
 * modal, both on GET /penalties/session. pill_only (owner rule, A-52): session pages show no payment
 * details, only the Payment review pill where Ops clear issues; nothing renders without issues.
 * link adds the Teacher Payments link and "View penalties here" (the Change penalty modal as a pop-up).
 */
export const LOAD_FAILED_TEXT = "Could not load this session's teacher payment penalties.";
export const REVIEW_LOAD_FAILED_TEXT = "Could not load this class's payment review.";

const SessionPaymentPenalties = ({
  session_uuid,
  teacher_uuid,
  teacher_name,
  new_request_id = uuidv4,
  pill_only = false,
  link = null
}) => {
  const [state, setState] = useState({ status: 'loading', view: null });
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  const hide = useCallback(() => {
    setOpen(false);
    setHidden(true);
  }, []);

  // keep: a re-read after a write leaves the rows on screen until the new ones land. If it fails, the
  // view stays only as the open modal's context; its rows are never offered again as current.
  const load = useCallback(
    (keep = false) => {
      seq.current += 1;
      const mine = seq.current;
      if (!keep) setState({ status: 'loading', view: null });
      fetch_penalties_session(session_params(session_uuid))
        .then((data) => {
          if (mine === seq.current) setState({ status: 'ok', view: session_view(data) });
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          if (hides_strip(e, keep)) {
            hide();
            return;
          }
          console.error(
            'payment penalties: session load failed',
            session_uuid,
            e?.response?.status || e?.message
          );
          setState((s) => ({ status: 'error', view: keep ? s.view : null }));
        });
    },
    [session_uuid, hide]
  );

  useEffect(() => {
    setHidden(false);
    setOpen(false);
    if (session_uuid) load();
  }, [session_uuid, load]);

  const { status, view } = state;
  const reload = () => load(true);

  if (link) {
    const can_open = shows_view_here({ session_uuid, hidden, status });
    return (
      <>
        <TeacherPaymentsLink
          teacher_uuid={link.teacher_uuid}
          epoch_start_time={link.epoch_start_time}
          // the page read may be minutes old: opening re-reads, keeping the rows until it lands
          on_view_here={
            can_open
              ? () => {
                  setOpen(true);
                  reload();
                }
              : null
          }
        />
        {!hidden && status === 'error' ? (
          <LoadFailed text={REVIEW_LOAD_FAILED_TEXT} on_retry={() => load()} />
        ) : null}
        {!hidden && status === 'ok' && view && view.issues.length ? (
          <div className="tp-sp tp-sp-pill">
            <ReviewPill
              issues={view.issues}
              app_join_text={view.app_join_text}
              teacher_names={view.teacher_names}
              post_action={post_issue_action}
              on_changed={reload}
              on_permission={hide}
            />
          </div>
        ) : null}
        {open && can_open ? (
          <ChangePenaltyModal
            view={view || session_view({ session: { uuid: session_uuid } })}
            teacher_uuid={teacher_uuid}
            teacher_name={teacher_name}
            new_request_id={new_request_id}
            load_failed_text={status === 'error' ? LOAD_FAILED_TEXT : ''}
            title={POPUP_TITLE}
            summary_text={popup_summary_text(status, view)}
            empty_text={POPUP_EMPTY_TEXT}
            list_title="Penalties"
            // xl: the tab's columns at the Penalties tab's width, so no penalty name breaks mid-word
            size="xl"
            on_reload={reload}
            on_permission={hide}
            on_close={() => setOpen(false)}
          />
        ) : null}
      </>
    );
  }

  if (!session_uuid || hidden) return null;

  if (pill_only) {
    if (status === 'error')
      return <LoadFailed text={REVIEW_LOAD_FAILED_TEXT} on_retry={() => load()} />;
    if (status !== 'ok' || !view || !view.issues.length) return null;
    return (
      <div className="tp-sp tp-sp-pill">
        <ReviewPill
          issues={view.issues}
          app_join_text={view.app_join_text}
          teacher_names={view.teacher_names}
          post_action={post_issue_action}
          on_changed={reload}
          on_permission={hide}
        />
      </div>
    );
  }

  return (
    <div className="tp-sp">
      <span className="tp-sp-title">Teacher payment penalties</span>
      {status === 'loading' ? <span className="tp-card-field-label">Loading…</span> : null}
      {status === 'error' ? <LoadFailed text={LOAD_FAILED_TEXT} on_retry={() => load()} /> : null}
      {status === 'ok' && view ? (
        <>
          <span className="tp-card-field-label">
            {view.rows.length
              ? `${view.rows.length} on this session · charged ${view.charged_text}`
              : 'None on this session'}
          </span>
          <ReviewPill
            issues={view.issues}
            app_join_text={view.app_join_text}
            teacher_names={view.teacher_names}
            post_action={post_issue_action}
            on_changed={reload}
            on_permission={hide}
          />
          <button type="button" className="tp-btn-out" onClick={() => setOpen(true)}>
            Change penalty
          </button>
        </>
      ) : null}
      {open && view ? (
        <ChangePenaltyModal
          view={view}
          teacher_uuid={teacher_uuid}
          teacher_name={teacher_name}
          new_request_id={new_request_id}
          load_failed_text={status === 'error' ? LOAD_FAILED_TEXT : ''}
          on_reload={reload}
          on_permission={hide}
          on_close={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default SessionPaymentPenalties;
